// Composition d'une adresse pour l'AFFICHAGE. Le stockage ne change pas : la base garde numero_voie,
// type_voie, nom_voie, code_postal, ville et pays separement. Ces fonctions decident seulement de ce
// que l'utilisateur lit.

/**
 * `numero_voie` porte un 0 sentinelle sur pres d'un millier d'adresses en base (938 au 21/07/2026,
 * de loin la valeur la plus frequente). Il signifie « pas de numero » — une place, un lieu-dit — et
 * non le numero zero, qui n'existe pas dans l'adressage francais. L'afficher produit « 0 Place
 * Barette », qui se lit comme une donnee fausse.
 *
 * Un 0 SUIVI d'un complement (`0 bis`, `0ter`) est conserve : c'est le complement qui porte alors
 * l'information, et le retirer perdrait une distinction reelle entre deux adresses voisines.
 */
const NUMERO_VOIE_SENTINELLE = /^0+$/;

export const numeroVoieAffiche = (numeroVoie?: string | number | null): string => {
  const valeur = String(numeroVoie ?? '').trim();
  return NUMERO_VOIE_SENTINELLE.test(valeur) ? '' : valeur;
};

/** Types de voie reconnus, pour separer « Rue d'Athenes » en type + nom. Minuscules et sans accent
 *  a la comparaison ; la casse d'origine est conservee dans ce qui est ecrit. */
const TYPES_VOIE = [
  'rue', 'avenue', 'boulevard', 'impasse', 'allee', 'place', 'chemin', 'route', 'quai', 'cours',
  'square', 'esplanade', 'passage', 'sentier', 'villa', 'promenade', 'rond-point', 'voie',
];

// \u0300-\u036f = marques diacritiques combinantes isolees par NFD (l'accent devient un caractere a
// part). Notation echappee et non litterale : ces caracteres sont invisibles dans un editeur.
const sansAccent = (valeur: string): string => valeur.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Separe le type de voie du nom : « Rue d'Athenes » -> { typeVoie: 'Rue', nomVoie: "d'Athenes" }.
 *
 * L'API IGN renvoie `street` type inclus. L'ecrire tel quel dans `nom_voie` sans toucher `type_voie`
 * laisse l'ancien type en place, et toute composition affiche alors le type deux fois
 * (« 8 Rue Rue d'Athenes »). La casse d'origine est conservee : la base contient deja des `Rue` et
 * des `rue`, en normaliser un seul cote creerait une troisieme variante.
 */
export const separerTypeVoie = (street?: string | null): { typeVoie: string; nomVoie: string } => {
  const valeur = String(street ?? '').trim();
  if (valeur === '') return { typeVoie: '', nomVoie: '' };
  const premierMot = valeur.split(/\s+/)[0];
  if (!TYPES_VOIE.includes(sansAccent(premierMot).toLowerCase())) {
    return { typeVoie: '', nomVoie: valeur };
  }
  return { typeVoie: premierMot, nomVoie: valeur.slice(premierMot.length).trim() };
};

/**
 * Voie seule : « 12 Rue des Lilas ». Le numero sentinelle est retire (cf. numeroVoieAffiche).
 *
 * Le type de voie n'est pas repete quand `nom_voie` commence deja par lui : 149 adresses portent le
 * type aux deux endroits (`type_voie` = « Rue », `nom_voie` = « Rue d'Athenes »), heritage d'un import
 * qui posait « Rue » par defaut combine a une saisie qui ecrivait le `street` IGN brut. Dedoublonner
 * a l'affichage repare ces lignes sans migration ; `separerTypeVoie` empeche d'en creer de nouvelles.
 */
export const ligneVoie = (
  numeroVoie?: string | number | null,
  typeVoie?: string | null,
  nomVoie?: string | null,
): string => {
  const nom = String(nomVoie ?? '').trim();
  const type = String(typeVoie ?? '').trim();
  const typeDejaDansNom =
    type !== '' && sansAccent(nom).toLowerCase().startsWith(`${sansAccent(type).toLowerCase()} `);
  return [numeroVoieAffiche(numeroVoie), typeDejaDansNom ? '' : type, nom].filter(Boolean).join(' ');
};

/**
 * Adresse complete sur UNE ligne : « 12 Rue des Lilas, 75001 Paris, France ».
 *
 * Les saisies passent desormais par un seul champ (la suggestion IGN renseigne code postal, ville et
 * pays sans les montrer) ; en lecture seule on recompose la ligne entiere, sinon on ne lirait que la
 * voie et l'adresse perdrait sa localisation. Chaque segment est filtre : une fiche ancienne peut
 * n'avoir que la voie, la ligne se contracte au lieu d'afficher des virgules orphelines.
 */
export const adresseUneLigne = (
  voie?: string | null,
  codePostal?: string | null,
  ville?: string | null,
  pays?: string | null,
): string =>
  [voie, [codePostal, ville].map((p) => String(p ?? '').trim()).filter(Boolean).join(' '), pays]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(', ');

/** Raccourci pour une entite `adresse` telle que renvoyee par l'API (voie eclatee en trois colonnes). */
export const adresseEntiteUneLigne = (adresse?: {
  numero_voie?: string | number | null;
  type_voie?: string | null;
  nom_voie?: string | null;
  code_postal?: string | null;
  ville?: string | null;
  pays?: string | null;
} | null): string =>
  adresseUneLigne(
    ligneVoie(adresse?.numero_voie, adresse?.type_voie, adresse?.nom_voie),
    adresse?.code_postal,
    adresse?.ville,
    adresse?.pays,
  );
